/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { FC, useCallback, useState } from 'react';
import { includes } from 'lodash';

import log from '@broadleaf/admin-components/dist/common/utils/log';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import { getActionStatus } from '../../../utils/RmFulfillmentUtils';
import RmFulfillmentAction from '../../components/RmFulfillmentAction';
import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal/index';

const logger = log.getLogger(
  'oms.components.FulfillmentView.action-components.SendShippingDocuments'
);

export interface SendShippingDocumentsProps {
  actionDefinition: IMetadataAction;
  dispatch: Function;
  metadata: IMetadata;
  state: IFulfillmentViewLocalState;
}

const SendShippingDocuments: FC<SendShippingDocumentsProps> = props => {
  const { actionDefinition, dispatch, metadata, state } = props;
  const { attributes = {}, label } = actionDefinition;
  const { allowedStatuses = [] } = attributes as Record<string, any>;

  const getShippingDocumentsEndpoint = getEndpointByType(
    actionDefinition,
    'SEND_SHIPPING_DOCUMENTS'
  );
  const contextParams = useContextParams(metadata, state);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const sendShippingDocuments = useCallback(async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      await request(
        { method: 'post', ...getShippingDocumentsEndpoint },
        contextParams
      );
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      logger.error(
        `An error occurred trying to send shipping documents for fulfillment ${contextParams.fulfillmentId}`
      );
    } finally {
      setLoading(false);
    }
  }, [
    contextParams,
    dispatch,
    getShippingDocumentsEndpoint,
    state.data.attributes
  ]);

  const { isDisabled, actionTooltip } = getActionStatus(
    'SEND_SHIPPING_DOCUMENTS',
    state.data
  );

  if (!includes(allowedStatuses, state.data.status)) {
    return <></>;
  }

  return (
    <>
      <RmFulfillmentAction
        disabled={state.isFetching || loading || isDisabled}
        onClick={() => {
          setIsOpen(true);
        }}
        tooltip={actionTooltip}
      >
        {label}
      </RmFulfillmentAction>
      {isOpen && (
        <ConfirmationModal
          onConfirm={async () => {
            setIsOpen(false);
            await sendShippingDocuments();
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export interface ConfirmationModalProps {
  onConfirm: () => void;
  onClose: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = props => {
  const { onClose } = props;
  return (
    <SimpleModal
      /*// @ts-ignore */
      closeOnClickOutside={true}
      isOpen
      onClose={onClose}
      size="sm"
      className="tw-"
      title={<>Confirm Notification</>}
    >
      <ModalBody {...props} />
    </SimpleModal>
  );
};

const ModalBody: React.FC<ConfirmationModalProps> = props => {
  const { onClose, onConfirm } = props;

  return (
    <div>
      <div className="tw-my-3">
        This action will email the seller. Please confirm this is intended.
      </div>
      <div className={'tw-flex tw-space-x-1'}>
        <button
          className={`focus:tw-shadow-outline tw-flex-1 tw-rounded tw-border tw-border-red-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-red-700 tw-shadow hover:tw-bg-red-100 focus:tw-bg-red-100 focus:tw-outline-none active:tw-bg-red-200 md:tw-mb-3`}
          onClick={() => {
            onClose();
          }}
          type="button"
        >
          Cancel
        </button>
        <button
          className={`focus:tw-shadow-outline tw-flex-1 tw-rounded tw-border tw-border-green-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-green-700 tw-shadow hover:tw-bg-green-100 focus:tw-bg-green-100 focus:tw-outline-none active:tw-bg-green-200 md:tw-mb-3`}
          onClick={() => {
            onConfirm();
          }}
          type="button"
        >
          Confirm
        </button>
      </div>
    </div>
  );
};

export default SendShippingDocuments;
export { SendShippingDocuments };
